import type { RJSFSchema } from '@rjsf/utils';
import dayjs from 'dayjs';

import { getCountryByName } from '@luxuryescapes/lib-countries';

import type {
  OpportunityRelatedDeal,
  OpportunityRelatedExtraGuestSurcharge,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

export type VendorBillingDetails = {
  billingcity?: string | null;
  billingcountry?: string | null;
  billingpostalcode?: string | null;
  billingstate?: string | null;
  billingstreet?: string | null;
  name?: string | null;
};

type PropertyLogoSource = {
  deleted_at?: unknown;
  logo_id_cloudinary_external?: string | null;
};

type GetMissingRequiredPropertyFieldsParams = {
  isCreatePropertyStep: boolean;
  propertyFormData: Record<string, unknown>;
  propertySchema: RJSFSchema | undefined;
};

type GetNextButtonLabelParams = {
  isAdvancingStep?: boolean;
  isCreatePropertyStep: boolean;
  isCreatingNewProperty: boolean;
  isCreatingRooms?: boolean;
  roomCreateCount?: number;
  isRoomStep?: boolean;
  isValidatingOpportunity: boolean;
};

type FlashRoomRateAssignment = {
  packageData: Record<string, unknown>;
  packageId: string;
};

type AppliedRoomRate = {
  ratePlanName?: string;
  roomName: string;
  roomRateId: string;
  roomTypeId: string;
};

type PackageOption = {
  fk_room_rate_id?: string | null;
  name?: string;
};

type RelatedPackageRoom = Pick<OpportunityRelatedDeal, 'id' | 'room_asset' | 'sfid'>;

// Defaults are based on averaged age ranges aggregated from svc-reservation data.
const DEFAULT_CHILD_MAX_AGE = 11;
const DEFAULT_INFANT_MAX_AGE = 1;

function parseAgeRangeMax(value: string | null | undefined) {
  const ages = value?.match(/\d+(?:\.\d+)?/g)?.map(Number);

  return ages?.length ? Math.floor(Math.max(...ages)) : undefined;
}

function normalizeAddressValue(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function normaliseRoomName(name: string | null | undefined) {
  return name?.trim().toLowerCase() ?? '';
}

function getPackagePersistId(offerPackage: App.Package) {
  const persistId = offerPackage.id_salesforce_external ?? offerPackage.le_package_id;

  return persistId ? String(persistId) : undefined;
}

function getPackageOptions(offerPackage: App.Package): Array<PackageOption> {
  return 'package_options' in offerPackage && Array.isArray(offerPackage.package_options)
    ? ([...offerPackage.package_options] as Array<PackageOption>)
    : [];
}

export function buildFlashPackageRoomRateAssignments({
  offerPackages = [],
  relatedPackageRooms = [],
  roomRates,
  roomTypes,
}: {
  offerPackages?: Array<App.Package>;
  relatedPackageRooms?: Array<RelatedPackageRoom>;
  roomRates: Array<AppliedRoomRate>;
  roomTypes: Array<Pick<App.RoomType, 'id' | 'name'>>;
}): Array<FlashRoomRateAssignment> {
  const roomRatesByRoomTypeId = new Map(roomRates.map((roomRate) => [roomRate.roomTypeId, roomRate]));
  const roomsWithRates = roomTypes.filter((roomType) => roomRatesByRoomTypeId.has(roomType.id));
  const relatedRoomByPackageId = new Map(
    relatedPackageRooms.flatMap((relatedRoom) => {
      const packageId = relatedRoom.sfid || relatedRoom.id;
      return packageId && relatedRoom.room_asset?.name ? [[String(packageId), relatedRoom.room_asset.name]] : [];
    }),
  );

  return offerPackages.flatMap((offerPackage) => {
    const packageId = getPackagePersistId(offerPackage);
    const roomAssetName = packageId ? relatedRoomByPackageId.get(packageId) : undefined;
    const roomType = roomsWithRates.find(
      (roomType) => normaliseRoomName(roomType.name) === normaliseRoomName(roomAssetName),
    );
    const roomRate = roomType ? roomRatesByRoomTypeId.get(roomType.id) : undefined;

    if (!packageId || !roomType || !roomRate) {
      return [];
    }

    const packageOptions = getPackageOptions(offerPackage);
    const hasPackageOption = packageOptions.some((option) => option.fk_room_rate_id === roomRate.roomRateId);
    const nextPackageOptions = hasPackageOption
      ? packageOptions
      : packageOptions.concat({
          fk_room_rate_id: roomRate.roomRateId,
          name: roomRate.ratePlanName || roomRate.roomName,
        });

    return [
      {
        packageId,
        packageData: {
          fk_room_type_id: roomType.id,
          fk_room_rate_id: roomRate.roomRateId,
          package_options: nextPackageOptions,
        },
      },
    ];
  });
}

export function getFlashRatePlanNameFromTravelStart(deals: Array<OpportunityRelatedDeal> = []) {
  const travelStarts = deals
    .map((deal) => deal.travel_start_date__c)
    .filter((value): value is string => !!value)
    .map((value) => dayjs(value))
    .filter((date) => date.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (travelStarts.length === 0) {
    return '';
  }

  return formatFlashRatePlanName(travelStarts[0].format('YYYY-MM-DD'));
}

export function formatFlashRatePlanName(date: Date | string) {
  return `Flash ${dayjs(date).format('MMM YY')}`;
}

function hasAddressValue(addressParts: Array<string>, value: string, countryCode?: string) {
  const normalizedValue = normalizeAddressValue(value);
  const normalizedCountryCode = countryCode?.toLowerCase();

  return addressParts.some((part) => {
    const normalizedPart = normalizeAddressValue(part);
    return (
      normalizedPart.includes(normalizedValue) ||
      normalizedValue.includes(normalizedPart) ||
      (normalizedCountryCode === 'us' && normalizedPart === 'usa') ||
      (!!normalizedCountryCode && normalizedPart === normalizedCountryCode)
    );
  });
}

export function mapExtraGuestSurchargeAgeRangesToPropertyFormData(
  extraGuestSurcharges: Array<OpportunityRelatedExtraGuestSurcharge> = [],
): Record<string, number | string> {
  let childMaxAge: number | undefined;
  let infantMaxAge: number | undefined;

  extraGuestSurcharges.forEach((surcharge) => {
    const ageRangeMax = parseAgeRangeMax(surcharge.age_range__c);
    const guestType = surcharge.extra_guest_type__c?.toLowerCase();

    if (ageRangeMax === undefined || !guestType) {
      return;
    }

    if (guestType.includes('child')) {
      childMaxAge = childMaxAge === undefined ? ageRangeMax : Math.max(childMaxAge, ageRangeMax);
    }

    if (guestType.includes('infant')) {
      infantMaxAge = infantMaxAge === undefined ? ageRangeMax : Math.max(infantMaxAge, ageRangeMax);
    }
  });

  const maxChildAge = childMaxAge ?? DEFAULT_CHILD_MAX_AGE;

  return {
    children_policy: `${maxChildAge} years & under`,
    max_child_age: maxChildAge,
    max_infant_age: infantMaxAge ?? DEFAULT_INFANT_MAX_AGE,
  };
}

export function mapVendorBillingToPropertyFormData(vendor?: VendorBillingDetails | null): Record<string, unknown> {
  const name = vendor?.name?.trim();
  const billingstreetRaw = vendor?.billingstreet?.trim();
  const fullAddressParts =
    billingstreetRaw
      ?.split(',')
      .map((part) => part.trim())
      .filter(Boolean) ?? [];
  const hasFullBillingstreet = fullAddressParts.length >= 5;
  const billingcity = vendor?.billingcity?.trim() || (hasFullBillingstreet ? fullAddressParts[1] : undefined);
  const billingcountry =
    vendor?.billingcountry?.trim() ||
    (hasFullBillingstreet ? fullAddressParts[fullAddressParts.length - 1] : undefined);
  const billingCountryCode = billingcountry ? getCountryByName(billingcountry)?.iso2 : undefined;
  const billingpostalcode =
    vendor?.billingpostalcode?.trim() ||
    (hasFullBillingstreet ? fullAddressParts[fullAddressParts.length - 2] : undefined);
  const billingstate =
    vendor?.billingstate?.trim() || (hasFullBillingstreet ? fullAddressParts[fullAddressParts.length - 3] : undefined);
  const addressParts = billingstreetRaw ? [billingstreetRaw] : [];
  const dedupeAddressParts = billingstreetRaw ? [...fullAddressParts] : [];
  [billingcity, billingstate, billingpostalcode, billingcountry].forEach((part) => {
    if (part && !hasAddressValue(dedupeAddressParts, part, part === billingcountry ? billingCountryCode : undefined)) {
      addressParts.push(part);
      dedupeAddressParts.push(part);
    }
  });
  const address = addressParts.join(', ');

  return {
    ...(name ? { name } : {}),
    ...(address ? { address } : {}),
    ...(billingcity ? { city: billingcity, location_heading: billingcity } : {}),
    ...(billingcountry ? { country: billingcountry, location_subheading: billingcountry } : {}),
  };
}

export function mapExistingPropertyLogoToPropertyFormData(
  properties: Array<PropertyLogoSource> = [],
): Record<string, string> {
  const logoId = properties.find(
    (property) => !property.deleted_at && property.logo_id_cloudinary_external,
  )?.logo_id_cloudinary_external;

  return logoId ? { logo_id_cloudinary_external: logoId } : {};
}

export function getMissingRequiredFieldLabels(schema: RJSFSchema | undefined, formData: Record<string, unknown>) {
  return (schema?.required ?? [])
    .filter((field) => {
      const value = formData[field];
      return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
    })
    .map((field) => {
      const propertySchema = schema?.properties?.[field];
      return typeof propertySchema === 'object' && propertySchema?.title ? propertySchema.title : field;
    });
}

export function getMissingRequiredPropertyFields({
  isCreatePropertyStep,
  propertyFormData,
  propertySchema,
}: GetMissingRequiredPropertyFieldsParams) {
  if (!isCreatePropertyStep) {
    return [];
  }

  return getMissingRequiredFieldLabels(propertySchema, propertyFormData);
}

export function getNextButtonLabel({
  isAdvancingStep,
  isCreatePropertyStep,
  isCreatingNewProperty,
  isCreatingRooms,
  isRoomStep,
  roomCreateCount = 0,
  isValidatingOpportunity,
}: GetNextButtonLabelParams) {
  if (isValidatingOpportunity) {
    return 'Checking...';
  }

  if (isAdvancingStep) {
    return 'Preparing rooms...';
  }

  if (isCreatingRooms) {
    return 'Creating rooms...';
  }

  if (isRoomStep && roomCreateCount > 0) {
    return `Create ${roomCreateCount} room${roomCreateCount === 1 ? '' : 's'} and next`;
  }

  if (!isCreatePropertyStep) {
    return 'Next';
  }

  if (isCreatingNewProperty) {
    return 'Creating...';
  }

  return 'Create property and next';
}
